import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Input, Select, Textarea } from '../../components/UI';
import { reEngagementAPI, leadsAPI, pipelineAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlineRefresh, HiOutlineTrash, HiOutlinePlus } from 'react-icons/hi';

const EMPTY_FILTERS = {
  city: '',
  source: '',
  status: '',
  stage: '',
  priority: '',
  minLeadScore: '',
  maxLeadScore: '',
  tags: '',
};

const STATUS_OPTIONS = [
  { value: 'lost', label: 'Lost' },
  { value: 'cold', label: 'Cold' },
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
  { value: 'not_connected', label: 'Not Connected' },
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STEP_TYPE_OPTIONS = [
  { value: 'call', label: 'Call (creates a follow-up task)' },
  { value: 'whatsapp', label: 'WhatsApp message' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'wait', label: 'Wait' },
];

const EMPTY_STEP = { type: 'call', delay_days: 0, message_text: '', call_script: '' };

const ReEngagementCampaignModal = ({ isOpen, onClose, campaign }) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(campaign?._id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [daysInactiveMin, setDaysInactiveMin] = useState('');
  const [daysInactiveMax, setDaysInactiveMax] = useState('');
  const [steps, setSteps] = useState([{ ...EMPTY_STEP }]);

  // Reset form state when the modal opens for a different campaign
  useEffect(() => {
    if (!isOpen) return;
    if (campaign) {
      setName(campaign.name || '');
      setDescription(campaign.description || '');
      setFilters({ ...EMPTY_FILTERS, ...(campaign.filters || {}) });
      setDaysInactiveMin(
        campaign.trigger_conditions?.days_since_last_contact_min ?? ''
      );
      setDaysInactiveMax(
        campaign.trigger_conditions?.days_since_last_contact_max ?? ''
      );
      setSteps(
        (campaign.steps || []).length > 0
          ? campaign.steps.map((s) => ({
              type: s.type,
              delay_days: s.delay_days || 0,
              message_text: s.message_text || '',
              call_script: s.call_script || '',
            }))
          : [{ ...EMPTY_STEP }]
      );
    } else {
      setName('');
      setDescription('');
      setFilters({ ...EMPTY_FILTERS });
      setDaysInactiveMin('');
      setDaysInactiveMax('');
      setSteps([{ ...EMPTY_STEP }]);
    }
  }, [isOpen, campaign]);

  // Sources list for the dropdown
  const { data: sources } = useQuery({
    queryKey: ['leadSources'],
    queryFn: async () => {
      const response = await leadsAPI.getSources();
      const list = response.data?.data || [];
      return list.map((s) => (typeof s === 'string' ? s : s.source || s.name)).filter(Boolean);
    },
    enabled: isOpen,
  });

  // Pipeline stages for the stage dropdown (dynamic per organization)
  const { data: stages } = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: async () => {
      const response = await pipelineAPI.getStats({ range: 'all' });
      const stats = response.data?.data || [];
      return stats.map((s) => ({ key: s.key || s.value, name: s.name })).filter((s) => s.key);
    },
    enabled: isOpen,
  });

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const buildFiltersPayload = () => {
    const payload = {};
    if (filters.city.trim()) payload.city = filters.city.trim();
    if (filters.source.trim()) payload.source = filters.source.trim();
    if (filters.status) payload.status = filters.status;
    if (filters.stage) payload.stage = filters.stage;
    if (filters.priority) payload.priority = filters.priority;
    if (filters.minLeadScore !== '') payload.minLeadScore = Number(filters.minLeadScore);
    if (filters.maxLeadScore !== '') payload.maxLeadScore = Number(filters.maxLeadScore);
    const tags = filters.tags.split(',').map((t) => t.trim()).filter(Boolean);
    if (tags.length > 0) payload.tags = tags;
    return payload;
  };

  const updateStep = (index, key, value) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };

  const saveMutation = useMutation({
    mutationFn: (data) =>
      isEdit ? reEngagementAPI.updateCampaign(campaign._id, data) : reEngagementAPI.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['reEngagementCampaigns']);
      toast.success(isEdit ? 'Campaign updated successfully' : 'Campaign created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to save campaign');
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    saveMutation.mutate({
      name: name.trim(),
      description,
      filters: buildFiltersPayload(),
      daysInactiveMin: daysInactiveMin === '' ? undefined : Number(daysInactiveMin),
      daysInactiveMax: daysInactiveMax === '' ? undefined : Number(daysInactiveMax),
      steps,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Re-engagement Campaign' : 'New Re-engagement Campaign'} size="lg">
      <div className="space-y-4">
        {/* Basic details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Campaign name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 90-day win-back calls"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Inactive from (days)"
              type="number"
              min="0"
              value={daysInactiveMin}
              onChange={(e) => setDaysInactiveMin(e.target.value)}
              placeholder="e.g. 60"
            />
            <Input
              label="Inactive up to (days)"
              type="number"
              min="0"
              value={daysInactiveMax}
              onChange={(e) => setDaysInactiveMax(e.target.value)}
              placeholder="e.g. 180"
            />
          </div>
        </div>

        <Textarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this win-back campaign for?"
        />

        {/* Trigger filters */}
        <div className="border border-secondary-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <HiOutlineRefresh className="w-5 h-5 text-primary-600" />
            <h3 className="text-sm font-bold text-secondary-900">Target leads by</h3>
            <span className="text-xs text-secondary-400">(leave a filter empty to ignore it)</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Status"
              placeholder="Any status"
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              options={STATUS_OPTIONS}
            />
            <Select
              label="Pipeline stage"
              placeholder="Any stage"
              value={filters.stage}
              onChange={(e) => setFilter('stage', e.target.value)}
              options={(stages || []).map((s) => ({ value: s.key, label: s.name }))}
            />
            <Input
              label="City"
              type="text"
              value={filters.city}
              onChange={(e) => setFilter('city', e.target.value)}
              placeholder="e.g. Dubai"
            />
            <Select
              label="Source"
              placeholder="Any source"
              value={filters.source}
              onChange={(e) => setFilter('source', e.target.value)}
              options={(sources || []).map((s) => ({ value: s, label: s }))}
            />
            <Input
              label="Min lead score (0-100)"
              type="number"
              min="0"
              max="100"
              value={filters.minLeadScore}
              onChange={(e) => setFilter('minLeadScore', e.target.value)}
              placeholder="e.g. 40"
            />
            <Input
              label="Max lead score (0-100)"
              type="number"
              min="0"
              max="100"
              value={filters.maxLeadScore}
              onChange={(e) => setFilter('maxLeadScore', e.target.value)}
              placeholder="e.g. 100"
            />
          </div>
        </div>

        {/* Steps sequence */}
        <div className="border border-secondary-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-secondary-900">Re-engagement steps</h3>
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={() => setSteps((prev) => [...prev, { ...EMPTY_STEP }])}
            >
              Add step
            </Button>
          </div>
          <p className="text-xs text-secondary-400">
            Running the campaign creates a follow-up <strong>call task</strong> for each targeted lead
            (from the first Call step). WhatsApp/SMS/Email steps are saved as message text on the task notes.
          </p>

          {steps.map((step, i) => (
            <div key={i} className="p-3 bg-secondary-50 rounded-lg border border-secondary-100 space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-secondary-500 w-6">#{i + 1}</span>
                <Select
                  value={step.type}
                  onChange={(e) => updateStep(i, 'type', e.target.value)}
                  options={STEP_TYPE_OPTIONS}
                  className="flex-1"
                />
                <Input
                  type="number"
                  min="0"
                  value={step.delay_days}
                  onChange={(e) => updateStep(i, 'delay_days', e.target.value)}
                  className="w-28"
                  placeholder="Delay (days)"
                />
                {steps.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                    onClick={() => setSteps((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                )}
              </div>
              {step.type === 'call' ? (
                <Textarea
                  rows={2}
                  value={step.call_script}
                  onChange={(e) => updateStep(i, 'call_script', e.target.value)}
                  placeholder="Call script for the telecaller (added to the follow-up task notes)…"
                />
              ) : (
                <Textarea
                  rows={2}
                  value={step.message_text}
                  onChange={(e) => updateStep(i, 'message_text', e.target.value)}
                  placeholder={`Message text for the ${step.type} step (saved on the task notes)…`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3 justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saveMutation.isPending}>
            {isEdit ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReEngagementCampaignModal;